import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";

const STORAGE_KEY = "cafeole-voice-bank-stash";

const getStored = () => {
  if (typeof window === "undefined") return { slugs: [], title: null };

  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return { slugs: [], title: null };

    const parsed = JSON.parse(stored);

    if (Array.isArray(parsed)) {
      return { slugs: parsed, title: null };
    }

    return {
      slugs: Array.isArray(parsed.slugs) ? parsed.slugs : [],
      title: typeof parsed.title === "string" ? parsed.title : null,
    };
  } catch {
    return { slugs: [], title: null };
  }
};

const VoiceBankStashContext = createContext(null);

export const VoiceBankStashProvider = ({ children }) => {
  const stashRef = useRef(getStored());
  const [stashSlugs, setStashSlugsState] = useState(stashRef.current.slugs);
  const [listTitle, setListTitleState] = useState(stashRef.current.title);

  useEffect(() => {
    const stored = getStored();
    stashRef.current = stored;
    setStashSlugsState(stored.slugs);
    setListTitleState(stored.title);
  }, []);

  const persist = () => {
    if (typeof window !== "undefined") {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(stashRef.current));
    }
  };

  const setStash = useCallback((slugs) => {
    stashRef.current = { ...stashRef.current, slugs };
    setStashSlugsState(slugs);
    persist();
  }, []);

  const setListTitle = useCallback((title) => {
    const nextTitle =
      typeof title === "function" ? title(stashRef.current.title) : title;
    const normalized =
      typeof nextTitle === "string" && nextTitle.length > 0 ? nextTitle : null;

    stashRef.current = { ...stashRef.current, title: normalized };
    setListTitleState(normalized);
    persist();
  }, []);

  const updateSlugs = (updater) => {
    setStashSlugsState((current) => {
      const next = updater(current);
      stashRef.current = { ...stashRef.current, slugs: next };
      persist();
      return next;
    });
  };

  const addToStash = (slug) => {
    updateSlugs((current) => {
      if (current.includes(slug)) return current;
      return [...current, slug];
    });
  };

  const removeFromStash = (slug) => {
    updateSlugs((current) => current.filter((itemSlug) => itemSlug !== slug));
  };

  const toggleStash = (slug) => {
    updateSlugs((current) =>
      current.includes(slug)
        ? current.filter((itemSlug) => itemSlug !== slug)
        : [...current, slug],
    );
  };

  const clearStash = useCallback(() => {
    setStash([]);
  }, [setStash]);

  const reorderStash = useCallback((fromIndex, toIndex) => {
    updateSlugs((current) => {
      if (
        fromIndex === toIndex ||
        fromIndex < 0 ||
        toIndex < 0 ||
        fromIndex >= current.length ||
        toIndex >= current.length
      ) {
        return current;
      }

      const next = [...current];
      const [moved] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, moved);
      return next;
    });
  }, []);

  const isStashed = (slug) => stashSlugs.includes(slug);

  return (
    <VoiceBankStashContext.Provider
      value={{
        stashSlugs,
        stashIds: stashSlugs,
        stashCount: stashSlugs.length,
        listTitle,
        setStash,
        setListTitle,
        addToStash,
        removeFromStash,
        toggleStash,
        clearStash,
        reorderStash,
        isStashed,
      }}
    >
      {children}
    </VoiceBankStashContext.Provider>
  );
};

export const useVoiceBankStash = () => {
  const context = useContext(VoiceBankStashContext);

  if (!context) {
    throw new Error(
      "useVoiceBankStash must be used within VoiceBankStashProvider",
    );
  }

  return context;
};
